import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import {
  Check,
  XCircle,
  RefreshCcw,
  Truck,
  X,
  Loader2,
  Search,
  Package,
  Mail,
} from "lucide-react";

export default function PhysicalCardRequests({ apiBase = "https://appapi.unpaidfinancial.com/api" }) {
  const http = useMemo(() => {
    const instance = axios.create({ baseURL: apiBase });
    instance.interceptors.request.use((cfg) => {
      const token =
        localStorage.getItem("accessToken") || localStorage.getItem("bank_token");
      if (token) cfg.headers.Authorization = `Bearer ${token}`;
      return cfg;
    });
    return instance;
  }, [apiBase]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [processing, setProcessing] = useState(null);
  const [filterEmail, setFilterEmail] = useState("");
  const [filterEtat, setFilterEtat] = useState("all");

  // --- helpers ---
  const normalizeEtat = (e) => {
    const s = String(e || "").toLowerCase();
    if (["livraison_en_cours", "en_livraison"].includes(s)) return "livraison_en_cours";
    if (["livree", "livrée"].includes(s)) return "livree";
    if (["commandee", "commandé", "en_attente"].includes(s)) return "commandee";
    if (["refusee", "refusée", "rejected"].includes(s)) return "refusee";
    return "aucune";
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

  const apiError = (e) =>
    e?.response?.data?.error || e?.response?.data?.message || e?.message || "Erreur API";

  // --- fetch ---
  const fetchData = useCallback(
    async (overridePage = null) => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await http.get("/bank/cards", {
          params: { page: overridePage ?? page, per_page: 20 },
        });

        const allCards = data?.data ?? data ?? [];
        const filtered = allCards.filter(
          (c) => c?.request_etat && normalizeEtat(c.request_etat) !== "aucune"
        );

        const filteredBySearch = filtered.filter((c) => {
          const user = c.account?.user ?? {};
          const etat = normalizeEtat(c.request_etat);
          const email = (user.email || "").toLowerCase();
          const matchEmail = !filterEmail || email.includes(filterEmail.toLowerCase());
          const matchEtat = filterEtat === "all" || etat === filterEtat;
          return matchEmail && matchEtat;
        });

        setRows(filteredBySearch);
        setTotal(filteredBySearch.length);
        setLastPage(data?.last_page ?? 1);
      } catch (e) {
        setErr(apiError(e));
      } finally {
        setLoading(false);
      }
    },
    [http, page, filterEmail, filterEtat]
  );

  useEffect(() => {
    fetchData();
  }, [page, filterEmail, filterEtat]);

  const refresh = () => fetchData();

  // --- actions ---
  const handleAction = async (cardId, action) => {
    setProcessing(cardId);
    setErr("");
    try {
      const url =
        action === "validate"
          ? `/bank/cards/${cardId}/validate`
          : action === "refuse"
          ? `/bank/cards/${cardId}/refuse`
          : `/bank/cards/${cardId}/deliver`; // ✅ nouvelle route pour livrée

      const { data } = await http.patch(url);
      setRows((prev) =>
        prev.map((c) =>
          c.id === cardId ? { ...c, request_etat: data.etat ?? action } : c
        )
      );
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setProcessing(null);
    }
  };

  const EtatBadge = ({ etat }) => {
    const st = normalizeEtat(etat);
    const map = {
      commandee: "bg-amber-100 text-amber-800",
      livraison_en_cours: "bg-blue-100 text-blue-800",
      livree: "bg-green-100 text-green-800",
      refusee: "bg-red-100 text-red-800",
    };
    const label = {
      commandee: "Commandée",
      livraison_en_cours: "En livraison",
      livree: "Livrée",
      refusee: "Refusée",
    }[st];
    return (
      <span
        className={`px-2 py-1 rounded-full text-[11px] font-medium ${
          map[st] || "bg-gray-100 text-gray-600"
        }`}
      >
        {label || st}
      </span>
    );
  };

  // --- UI ---
  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Demandes de cartes physiques</h1>
          <p className="text-sm text-gray-500">
            Liste des demandes à traiter • Total : <b>{total}</b>
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="flex items-center gap-2 px-4 h-10 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCcw size={16} />
          Actualiser
        </button>
      </div>

      {/* Filtres */}
      <div className="bg-white border rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px] text-gray-500">Email client</label>
            <div className="flex items-center border rounded-lg px-2">
              <Mail size={14} className="text-gray-400" />
              <input
                type="text"
                placeholder="ex: client@banque.com"
                value={filterEmail}
                onChange={(e) => setFilterEmail(e.target.value)}
                className="h-9 w-56 border-0 focus:ring-0 px-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] text-gray-500">État</label>
            <select
              value={filterEtat}
              onChange={(e) => setFilterEtat(e.target.value)}
              className="h-9 border rounded-lg px-3 text-sm"
            >
              <option value="all">Tous</option>
              <option value="commandee">Commandée</option>
              <option value="livraison_en_cours">En livraison</option>
              <option value="livree">Livrée</option>
              <option value="refusee">Refusée</option>
            </select>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => {
                setFilterEmail("");
                setFilterEtat("all");
              }}
              className="px-3 h-9 rounded-lg border bg-white hover:bg-gray-50 text-sm"
            >
              Réinitialiser
            </button>
            <button
              onClick={refresh}
              disabled={loading}
              className="px-4 h-9 rounded-lg bg-black text-white text-sm hover:bg-gray-800 disabled:opacity-60 flex items-center gap-1"
            >
              <Search size={14} />
              Filtrer
            </button>
          </div>
        </div>
      </div>

      {/* Erreur */}
      {err && (
        <div className="mb-3 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {String(err)}
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="py-10 text-center text-gray-400 flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" /> Chargement…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-gray-500 border rounded-xl bg-white">
          Aucune demande ne correspond à vos filtres.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {rows.map((card) => {
            const u = card.account?.user ?? {};
            const etat = normalizeEtat(card.request_etat);
            const name =
              `${u.prenom ?? ""} ${u.nom ?? ""}`.trim() || u.name || "Client inconnu";
            const email = u.email || "—";
            const addr = u.adresse || "Adresse non renseignée";
            const date = fmtDate(card.created_at);

            return (
              <article
                key={card.id}
                className="p-4 border bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-150 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <EtatBadge etat={etat} />
                    <span className="text-xs text-gray-500">{date}</span>
                  </div>

                  <div className="space-y-1 text-sm">
                    <div className="font-medium text-gray-900">{name}</div>
                    <div className="text-gray-500 text-[13px]">{email}</div>
                    <div className="text-gray-600 text-[13px] mt-1 line-clamp-2">
                      {addr}
                    </div>
                  </div>
                </div>

                {/* Boutons */}
                <div className="mt-4 flex flex-col gap-2">
                  {etat === "commandee" && (
                    <div className="flex gap-2">
                      <button
                        style={{backgroundColor:"green"}}
                        onClick={() => handleAction(card.id, "validate")}
                        disabled={processing === card.id}
                        className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white rounded-lg px-3 py-2 hover:bg-green-700 disabled:opacity-50"
                      >
                        {processing === card.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Check size={16} />
                        )}
                        Valider
                      </button>
                      <button
                        onClick={() => handleAction(card.id, "refuse")}
                        disabled={processing === card.id}
                        className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white rounded-lg px-3 py-2 hover:bg-red-700 disabled:opacity-50"
                      >
                        {processing === card.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <XCircle size={16} />
                        )}
                        Refuser
                      </button>
                    </div>
                  )}

                  {etat === "livraison_en_cours" && (
                    <button
                    style={{backgroundColor:"green"}}
                      onClick={() => handleAction(card.id, "deliver")}
                      disabled={processing === card.id}
                      className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white rounded-lg px-3 py-2 hover:bg-blue-700 disabled:opacity-50"
                    >
                      {processing === card.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Truck size={16} />
                      )}
                      Marquer comme livrée
                    </button>
                  )}

                  {etat === "livree" && (
                    <div className="flex items-center justify-center flex-1 text-green-700 bg-green-50 rounded-lg py-2 gap-2">
                      <Check size={16} />
                      Carte livrée
                    </div>
                  )}

                  {etat === "refusee" && (
                    <div className="flex items-center justify-center flex-1 text-red-700 bg-red-50 rounded-lg py-2 gap-2">
                      <X size={16} />
                      Refusée
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!loading && rows.length > 0 && (
        <div className="mt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm">
          <div className="text-gray-600">
            Total : <b>{total}</b> • Page <b>{page}</b> / {lastPage}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-4 h-10 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-40"
            >
              Précédent
            </button>
            <button
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              disabled={page >= lastPage || loading}
              className="px-4 h-10 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-40"
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
